import { OverlayModule } from '@angular/cdk/overlay';
import { ComponentPortal, PortalModule } from '@angular/cdk/portal';
import {
    afterRenderEffect,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    Injector,
    OnInit,
    signal,
    untracked,
    ViewEncapsulation,
} from '@angular/core';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { IBadgeMessageNotification } from 'app/core/models/message/notification-message';
import { EAclFunction, EAclModule } from 'app/core/permissions/enum/access.enum';
import { KiwidPermissionsModule } from 'app/core/permissions/kiwid-permissions.module';
import { hasPermission } from 'app/core/permissions/service/kiwid-permissions.service';
import { NotificationMessageSocketService } from 'app/core/services/message/notification-message-socket.service';
import { BaseComponent } from 'app/layout/common/base/base.component';
import { ClinicMessageNotificationComponent } from 'app/layout/common/messages/clinic-message-notification/clinic-message-notification.component';
import { ISidebarConfig } from 'app/layout/components/sidebar/sidebar-config.interface';
import { SidebarService } from 'app/layout/components/sidebar/sidebar.service';
import { first, Subscription } from 'rxjs';
import { locale as english } from './i18n/en';
import { locale as spanish } from './i18n/es';
import { locale as portuguese } from './i18n/pt';

@Component({
    selector: 'messages',
    templateUrl: './messages.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'messages',
    imports: [RouterModule, OverlayModule, PortalModule, MatButtonModule, MatIconModule, MatTooltipModule, MatBadgeModule, KiwidPermissionsModule],
})
export class MessagesComponent extends BaseComponent implements OnInit {
    private readonly injector = inject(Injector);
    private readonly sidebarService = inject(SidebarService);
    private readonly notificationMessageSocketService = inject(NotificationMessageSocketService);

    protected readonly hasPermissionChat = signal(
        hasPermission({
            routeAccess: [
                {
                    mod: EAclModule.CHAT,
                    fns: [EAclFunction.LIST],
                },
            ],
        }),
    ).asReadonly();
    private readonly badgeMessage = signal<IBadgeMessageNotification | null>(null);
    protected readonly totalBadgeMessage = computed(() => {
        const count = this.badgeMessage()?.totalBadge ?? 0;
        return count > 0 ? count : undefined;
    });

    constructor() {
        super();
        this.registerOnTranslate('CHAT', portuguese, english, spanish, false);
    }

    ngOnInit(): void {
        this.getData();
    }

    protected openClinicMessage(): void {
        const config: ISidebarConfig = {
            component: new ComponentPortal<ClinicMessageNotificationComponent>(ClinicMessageNotificationComponent),
            typeHeader: 'primary',
            title: this.translateKey('SIDEBAR.TITLE'),
            padding: false,
            insideScroll: true,
        };
        this.sidebarService.registerSidebar(config);

        this.subscriptions.add(
            this.sidebarService
                .componentRefObservable<ClinicMessageNotificationComponent>()
                .pipe(first())
                .subscribe((ref) => {
                    ref.instance.dataSidebar = signal({});
                }),
        );
        this.sidebarService.open();
    }

    private getData(): void {
        const getAllBadge = () => {
            const subscriptions: Subscription[] = [];

            const runOnSubscribe = () => {
                subscriptions.forEach((subscription) => subscription.unsubscribe());
            };

            const getData = () => {
                runOnSubscribe();

                subscriptions.push(
                    this.notificationMessageSocketService.badgeNotification.subscribe({
                        next: (data) => {
                            this.badgeMessage.set(data);
                        },
                    }),
                );

                subscriptions.push(
                    this.notificationMessageSocketService
                        .emitJoinBadgeMessageNotification()
                        .pipe(first())
                        .subscribe({
                            next: (data) => {
                                this.badgeMessage.set(data);
                            },
                        }),
                );

                const subscriptionDestroy = new Subscription();
                subscriptionDestroy.add({
                    unsubscribe: () => {
                        this.notificationMessageSocketService.emitLeaveBadge();
                    },
                });
                subscriptions.push(subscriptionDestroy);
            };

            this.subscriptions.add({
                unsubscribe: () => runOnSubscribe(),
            });

            afterRenderEffect(
                () => {
                    const isConnected = this.notificationMessageSocketService.isConnected();

                    untracked(() => {
                        if (isConnected) {
                            getData();
                        } else {
                            runOnSubscribe();
                        }
                    });
                },
                {
                    injector: this.injector,
                },
            );
        };

        getAllBadge();
    }
}
